import React, { useMemo, useState } from 'react'
import {
  Search,
  SearchWithFilter,
  SEARCH_PREVIEW_STATE_LABELS,
  SEARCH_PREVIEW_STATES,
  type FilterSelection,
  type SearchPreviewState,
} from '@enamelai/design-system'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './SearchShowcase.css'

const SAMPLE_FILTERS: Record<string, string[]> = {
  Providers: ['Dr. John Smith', 'Dr. Jane Doe', 'Dr. Alex Lee'],
  Operatories: ['Room 1', 'Room 2', 'Room 3'],
  Status: ['Confirmed', 'Pending', 'Cancelled'],
}

const SAMPLE_FILTER_LABELS: Record<string, string> = {
  Providers: 'Providers',
  Operatories: 'Operatories',
  Status: 'Status',
}

const formatSelectionSummary = (selected: FilterSelection): string => {
  const parts = Object.entries(selected)
    .filter(([values]) => values.length > 0)
    .map(
      ([key, values]) =>
        `${SAMPLE_FILTER_LABELS[key] ?? key}: ${values.join(', ')}`,
    )

  return parts.length > 0 ? parts.join(' · ') : 'No filters selected'
}

const SearchShowcase: React.FC = () => {
  const [value, setValue] = useState('')
  const [placeholder, setPlaceholder] = useState('Search appointment')
  const [disabled, setDisabled] = useState(false)
  const [showFilter, setShowFilter] = useState(true)
  const [showReset, setShowReset] = useState(true)
  const [selectedFilters, setSelectedFilters] = useState<FilterSelection>({})
  const [filterOpen, setFilterOpen] = useState(false)
  const [highlightedState, setHighlightedState] =
    useState<SearchPreviewState>('with-filter')

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'search-highlight-state',
            label: 'Highlighted state',
            control: (
              <select
                id="search-highlight-state"
                className="ui-field-control"
                value={highlightedState}
                onChange={(event) =>
                  setHighlightedState(event.target.value as SearchPreviewState)
                }
              >
                {SEARCH_PREVIEW_STATES.map((option) => (
                  <option key={option} value={option}>
                    {SEARCH_PREVIEW_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'search-placeholder',
            label: 'Placeholder',
            control: (
              <input
                id="search-placeholder"
                className="ui-field-control"
                type="text"
                value={placeholder}
                onChange={(event) => setPlaceholder(event.target.value)}
              />
            ),
          },
        ],
      },
      {
        title: 'State',
        fields: [
          {
            id: 'search-disabled',
            label: 'Disabled',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="search-disabled"
                  type="checkbox"
                  checked={disabled}
                  onChange={(event) => setDisabled(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'search-filter',
            label: 'Show filter',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="search-filter"
                  type="checkbox"
                  checked={showFilter}
                  onChange={(event) => {
                    setShowFilter(event.target.checked)
                  }}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'search-reset',
            label: 'Reset in menu',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="search-reset"
                  type="checkbox"
                  checked={showReset}
                  onChange={(event) => setShowReset(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [disabled, highlightedState, placeholder, showFilter, showReset],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Search"
        description="Compact search input with optional dynamic filter menu (accordion + checkboxes) for use across modules."
        stageClassName={`ui-preview__stage--wide search-showcase__stage-wrap`}
      >
        <div className={`search-showcase__sheet`}>
          <section className={`ui-showcase-live`}>
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Search with filter uses sample Providers, Operatories, and Status
              options. Click the filter icon to open the menu.
            </p>

            {showFilter ? (
              <SearchWithFilter
                value={value}
                placeholder={placeholder}
                disabled={disabled}
                filters={SAMPLE_FILTERS}
                filterLabels={SAMPLE_FILTER_LABELS}
                selectedFilters={selectedFilters}
                onFilterChange={setSelectedFilters}
                showResetButton={showReset}
                onReset={() => setSelectedFilters({})}
                filterOpen={filterOpen}
                onFilterOpenChange={setFilterOpen}
                onChange={setValue}
                onClear={() => setValue('')}
              />
            ) : (
              <Search
                value={value}
                placeholder={placeholder}
                disabled={disabled}
                onChange={setValue}
                onClear={() => setValue('')}
              />
            )}

            {showFilter ? (
              <p className="search-showcase__selection">
                {formatSelectionSummary(selectedFilters)}
              </p>
            ) : null}
          </section>

          <section className="search-showcase__matrix">
            {SEARCH_PREVIEW_STATES.map((previewState) => (
              <div
                key={previewState}
                className={`search-showcase__row${
                  previewState === highlightedState
                    ? ' search-showcase__row--selected'
                    : ''
                }`}
              >
                <h4 className="search-showcase__label">
                  {SEARCH_PREVIEW_STATE_LABELS[previewState]}
                </h4>
                <Search previewState={previewState} />
              </div>
            ))}
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel componentName="Search" sections={propertySections} />
    </>
  )
}

export default SearchShowcase
